import React, { useEffect, useRef, useState } from "react";

export interface WorkflowStep {
  name: string;
  label: string;
  action: string;
  status: "completed" | "pending" | "upcoming";
  timestamp?: string;
  refColumnName?: string;
}

interface TimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleNo: string;
  workflowSteps: WorkflowStep[];
  rowData: Record<string, any>;
  onWorkflowStepClick?: (step: WorkflowStep, rowData: Record<string, any>) => void;
  anchorElement?: HTMLElement | null;
}

export const TimelineModal: React.FC<TimelineModalProps> = ({
  isOpen,
  onClose,
  vehicleNo,
  workflowSteps,
  rowData,
  onWorkflowStepClick,
  anchorElement,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Handle outside clicks
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the popup
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        // Also check if click is not on the anchor element
        if (anchorElement && anchorElement.contains(event.target as Node)) {
          return; // Don't close if clicking on the anchor element
        }
        onClose();
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorElement]);

  // Reset option refs array when workflow steps change
  useEffect(() => {
    stepRefs.current = new Array(workflowSteps.length).fill(null);
  }, [workflowSteps.length]);

  // Initialize highlighted index when modal opens
  useEffect(() => {
    if (isOpen && workflowSteps.length > 0) {
      // Find first clickable step
      const firstClickableIndex = workflowSteps.findIndex(
        step => step.status === "completed" || step.status === "pending"
      );
      setHighlightedIndex(firstClickableIndex >= 0 ? firstClickableIndex : 0);
    } else if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen, workflowSteps.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      // Get indices of clickable steps
      const clickableIndices = workflowSteps
        .map((step, idx) => ({ step, idx }))
        .filter(({ step }) => step.status === "completed" || step.status === "pending")
        .map(({ idx }) => idx);
      
      if (clickableIndices.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        
        // Find current position in clickable indices
        const currentPos = clickableIndices.indexOf(highlightedIndex);
        const nextPos = currentPos >= 0 && currentPos < clickableIndices.length - 1
          ? currentPos + 1
          : 0; // Wrap to first
        
        const nextIndex = clickableIndices[nextPos];
        setHighlightedIndex(nextIndex);
        setTimeout(() => {
          const stepElement = stepRefs.current[nextIndex];
          if (stepElement) {
            stepElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            stepElement.focus();
          }
        }, 0);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        
        // Find current position in clickable indices
        const currentPos = clickableIndices.indexOf(highlightedIndex);
        const prevPos = currentPos > 0
          ? currentPos - 1
          : clickableIndices.length - 1; // Wrap to last
        
        const prevIndex = clickableIndices[prevPos];
        setHighlightedIndex(prevIndex);
        setTimeout(() => {
          const stepElement = stepRefs.current[prevIndex];
          if (stepElement) {
            stepElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            stepElement.focus();
          }
        }, 0);
        return;
      }

      if (event.key === 'Enter' && highlightedIndex >= 0) {
        event.preventDefault();
        event.stopPropagation();
        const step = workflowSteps[highlightedIndex];
        if ((step.status === "completed" || step.status === "pending") && onWorkflowStepClick) {
          onWorkflowStepClick(step, rowData);
        }
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, workflowSteps, highlightedIndex, rowData, onWorkflowStepClick]);

  if (!isOpen) return null;

  // Calculate position relative to anchor element
  const getPosition = () => {
    if (!anchorElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const rect = anchorElement.getBoundingClientRect();
    return {
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      transform: 'none',
    };
  };

  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return "Not completed";
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        // Try parsing as different format
        return timestamp;
      }
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return timestamp;
    }
  };

  const getStatusColor = (status: "completed" | "pending" | "upcoming") => {
    switch (status) {
      case "completed":
        return "bg-primary-500";
      case "pending":
        return "bg-orange-500";
      case "upcoming":
        return "bg-gray-300";
      default:
        return "bg-gray-300";
    }
  };

  const getStatusTextColor = (status: "completed" | "pending" | "upcoming") => {
    switch (status) {
      case "completed":
        return "text-primary-700 dark:text-primary-300";
      case "pending":
        return "text-orange-700 dark:text-orange-300";
      case "upcoming":
        return "text-gray-500 dark:text-gray-400";
      default:
        return "text-gray-500 dark:text-gray-400";
    }
  };

  // Extract timestamps from row data
  const getStepTimestamp = (step: WorkflowStep): string | undefined => {
    // Try various timestamp field patterns
    const patterns = [
      `${step.refColumnName?.replace("_id", "")}_created_at`,
      `${step.refColumnName?.replace("_id", "")}_createdAt`,
      `${step.refColumnName?.replace("_id", "")}_date`,
      `${step.action.toLowerCase().replace(/\s+/g, "_")}_created_at`,
      `${step.action.toLowerCase().replace(/\s+/g, "_")}_date`,
      `created_at`,
      `createdAt`,
    ];

    for (const pattern of patterns) {
      const value = rowData[pattern] || 
                    rowData[pattern.toLowerCase()] || 
                    rowData[pattern.toUpperCase()];
      if (value) {
        return String(value);
      }
    }

    // If step is completed, use a fallback timestamp
    if (step.status === "completed" && rowData.created_at) {
      return String(rowData.created_at);
    }

    return undefined;
  };

  const position = getPosition();

  return (
    <>
      {/* Backdrop overlay - always show for outside click detection */}
      <div
        className="fixed inset-0 bg-transparent z-[9998] transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Popover/Dropdown */}
      <div
        ref={popupRef}
        className="fixed z-[9999] bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden transition-all duration-200 min-w-[320px] max-w-[400px]"
        style={{
          ...position,
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="timeline-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            <div>
              <h3
                id="timeline-title"
                className="text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                Timeline
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {vehicleNo}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-3 py-2 max-h-[400px] overflow-y-auto">
          <div className="space-y-2" role="listbox">
            {workflowSteps.map((step, index) => {
              const timestamp = getStepTimestamp(step);
              // Allow clicking on all completed steps (for edit mode navigation)
              const isClickable = step.status === "completed" || step.status === "pending";
              const isHighlighted = highlightedIndex === index;

              return (
                <button
                  key={step.name}
                  ref={(el) => {
                    stepRefs.current[index] = el;
                  }}
                  onClick={() => {
                    if (isClickable && onWorkflowStepClick) {
                      onWorkflowStepClick(step, rowData);
                    }
                  }}
                  disabled={!isClickable}
                  tabIndex={isClickable ? 0 : -1}
                  role="option"
                  aria-selected={isHighlighted}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                    isHighlighted && isClickable
                      ? "bg-primary-200 dark:bg-primary-800/70 text-primary-900 dark:text-primary-100"
                      : isClickable
                        ? "hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        : "cursor-default opacity-60"
                  }`}
                  onMouseEnter={() => {
                    if (isClickable) {
                      setHighlightedIndex(index);
                    }
                  }}
                >
                  {/* Status indicator */}
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(step.status)}`}></div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-medium truncate ${getStatusTextColor(step.status)}`}>
                        {step.label}
                      </span>
                      {timestamp && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatTimestamp(timestamp)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
